import React from 'react';
import { Bar } from 'react-chartjs-2';
import { LoadingSpinner, ErrorMessage, EmptyState } from '../ui';
import { useTheme } from '../../contexts/ThemeContext';
import { getChartBackgroundColor, getChartJsOptions, getChartColors } from '../../utils/themeHelpers';
import { getResponsiveXAxisTicks, isMobileChartViewport } from '../../utils/chartResponsive';
import { zeroBaselinePlugin } from '../../utils/chartPlugins';
import { CHART_GUIDES } from '../../constants/chartGuides';
import ChartControls from '../ui/ChartControls';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import useChartGuide from '../../hooks/useChartGuide';
import {
  activeSeries,
  calculateLinearRegression,
  getControllerButtonClass,
  getPositiveAxisBounds,
  getSortedPurchaseData,
  getZeroLineGrid,
  numeric,
  toMonthLabel,
  useCustomerPurchaseBehaviorData,
  valueLabelPlugin,
  CustomerPurchaseBehaviorChartProps,
} from './customerPurchaseBehaviorData';
import { formatNumber } from '../../utils/formatters';

const CustomerPurchaseBehaviorGroupCountByMonthChart: React.FC<CustomerPurchaseBehaviorChartProps> = ({
  branchId,
  purchaseData,
  onRetry,
}) => {
  const { currentTheme } = useTheme();
  const chartColors = getChartColors(currentTheme);
  const countGroupLabels = [...activeSeries.map((series) => series.label), 'کل سفارش‌ها'];
  const groupCountByMonthChartRef = React.useRef<HTMLDivElement>(null);
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  const { openGuide } = useChartGuide();
  const [selectedCountGroupLabel, setSelectedCountGroupLabel] = React.useState<string>('');
  const { data: fetchedData, isLoading, error, refetch } = useCustomerPurchaseBehaviorData(branchId, {
    enabled: purchaseData === undefined,
  });
  const data = purchaseData ?? fetchedData;
  const showLoading = purchaseData === undefined && isLoading;
  const queryError = purchaseData === undefined ? error : null;
  const retry = onRetry ?? refetch;
  const sortedData = getSortedPurchaseData(data);
  const isMobileChart = isMobileChartViewport();

  React.useEffect(() => {
    if (!sortedData.length) {
      return;
    }

    if (!selectedCountGroupLabel) {
      setSelectedCountGroupLabel(countGroupLabels[0]);
      return;
    }
    if (!countGroupLabels.includes(selectedCountGroupLabel)) {
      setSelectedCountGroupLabel(countGroupLabels[0]);
    }
  }, [selectedCountGroupLabel, countGroupLabels, sortedData.length]);

  if (!branchId) {
    return (
      <div dir="rtl" className="card-base card-padding border border-status-warning" role="alert">
        برای دیدن این داشبورد، ابتدا شعبه را انتخاب کنید.
      </div>
    );
  }

  if (showLoading) {
    return <LoadingSpinner variant="dashboard" message="در حال بارگذاری رفتار خرید مشتریان…" />;
  }

  if (queryError) {
    return (
      <div className="card-base card-padding">
        <ErrorMessage
          message="دریافت اطلاعات رفتار خرید مشتریان با خطا مواجه شد. لطفاً دوباره تلاش کنید."
          onRetry={() => {
            retry();
          }}
        />
      </div>
    );
  }

  if (!sortedData.length) {
    return (
      <div className="card-base card-padding">
        <EmptyState message="برای این شعبه هنوز اطلاعات رفتار خریدی ثبت نشده است" />
      </div>
    );
  }

  const labels = sortedData.map(item => toMonthLabel(item.YearMonth));

  const selectedCountGroupIndex = activeSeries.findIndex((series) => series.label === selectedCountGroupLabel);
  const isTotalCountGroupSelected = selectedCountGroupLabel === countGroupLabels[countGroupLabels.length - 1];
  const totalGroupColorIndex = 7;
  const selectedCountGroupColorIndex = isTotalCountGroupSelected
    ? totalGroupColorIndex
    : Math.max(selectedCountGroupIndex, 0);

  const selectedGroupCountByMonth = isTotalCountGroupSelected
    ? sortedData.map((item) => activeSeries.reduce((sum, series) => sum + numeric(item[series.key]), 0))
    : sortedData.map((item) => (selectedCountGroupIndex >= 0 ? numeric(item[activeSeries[selectedCountGroupIndex].key]) : 0));

  const selectedGroupCountRegressionByMonth = calculateLinearRegression(selectedGroupCountByMonth);
  const selectedGroupCountAxisBounds = getPositiveAxisBounds(
    [...selectedGroupCountByMonth, ...selectedGroupCountRegressionByMonth],
    5
  );

  return (
    <div ref={groupCountByMonthChartRef} className="card-base card-padding animate-enter animate-delay-3">
      <ChartControls
        componentId="customer-purchase-behavior-group-count-by-month"
        componentTitle="روند ماهانهٔ یک گروه"
        componentType="chart"
        dashboardSource="customerPurchaseBehavior"
        onInfo={() => openGuide(CHART_GUIDES.purchaseBehaviorGroupTrend)}
        onFavorite={() => handleFavoriteToggle({
          componentId: 'customer-purchase-behavior-group-count-by-month',
          componentTitle: 'روند ماهانهٔ یک گروه',
          dashboardSource: 'customerPurchaseBehavior',
          componentType: 'chart',
          description: 'نمایش تعداد هر گروه مشتری در ماه‌های مختلف',
          props: { branchId },
        })}
        isFavorited={isFavorited('customer-purchase-behavior-group-count-by-month')}
        chartRef={groupCountByMonthChartRef}
        className="mb-4"
      />
      <h3 className="title-card">روند ماهانهٔ یک گروه</h3>
      <div className="mb-4 border rounded-lg p-3 md:p-4 bg-slate-50/60">
        <div className="mb-2 text-xs md:text-sm font-semibold text-text-secondary text-center">
          انتخاب گروه مشتری
        </div>
        <div role="group" aria-label="انتخاب گروه مشتری" className="cpb-month-picker mb-3 lg:flex-wrap lg:items-center lg:justify-center">
          {countGroupLabels.map((groupLabel) => {
            const isActive = groupLabel === selectedCountGroupLabel;
            return (
              <button
                key={groupLabel}
                type="button"
                onClick={() => setSelectedCountGroupLabel(groupLabel)}
                className={getControllerButtonClass(isActive, true)}
                title={`نمایش تعداد ${groupLabel} در ماه‌های مختلف`}
                aria-pressed={isActive}
              >
                {groupLabel}
              </button>
            );
          })}
        </div>
      </div>
      <div className="border rounded-lg p-3 md:p-4">
        <h4 className="text-sm md:text-base font-semibold mb-3 text-text-primary text-center">
          {`تعداد ماهانهٔ ${selectedCountGroupLabel} همراه با خط روند`}
        </h4>
        <div
          role="img"
          aria-label={`نمودار تعداد ماهانهٔ ${selectedCountGroupLabel} همراه با خط روند`}
          className="mobile-chart-host mobile-chart-host--category-bars-tall w-full"
        >
          <Bar
            plugins={[...(isMobileChart ? [zeroBaselinePlugin as any] : [valueLabelPlugin as any, zeroBaselinePlugin as any])]}
            data={{
              labels,
              datasets: [
                {
                  type: 'bar',
                  label: `تعداد ${selectedCountGroupLabel}`,
                  data: selectedGroupCountByMonth,
                  order: 3,
                  backgroundColor: getChartBackgroundColor(selectedCountGroupColorIndex, currentTheme, 0.75),
                  borderColor: chartColors[selectedCountGroupColorIndex % chartColors.length],
                  borderWidth: 1,
                  borderRadius: 6,
                  barPercentage: 0.9,
                  categoryPercentage: 0.86,
                  maxBarThickness: 54,
                  isPercent: false,
                } as any,
                {
                  type: 'line',
                  label: 'خط روند',
                  data: selectedGroupCountRegressionByMonth,
                  order: 1,
                  borderColor: '#dc2626',
                  backgroundColor: 'rgba(220, 38, 38, 0.2)',
                  borderWidth: 3.5,
                  borderDash: [],
                  pointRadius: 4,
                  pointHoverRadius: 5,
                  pointStyle: 'circle',
                  pointBackgroundColor: '#dc2626',
                  pointBorderColor: '#ffffff',
                  pointBorderWidth: 1.5,
                  tension: 0,
                  fill: false,
                  yAxisID: 'y',
                } as any,
              ] as any,
            }}
            options={{
              ...getChartJsOptions(currentTheme),
              responsive: true,
              maintainAspectRatio: false,
              interaction: {
                mode: 'index',
                intersect: false,
              },
              scales: {
                x: {
                  ...getChartJsOptions(currentTheme).scales?.x,
                  ticks: getResponsiveXAxisTicks(
                    getChartJsOptions(currentTheme).scales?.x?.ticks as Record<string, unknown> | undefined
                  ),
                },
              y: {
                ...getChartJsOptions(currentTheme).scales?.y,
                grid: getZeroLineGrid(getChartJsOptions(currentTheme).scales?.y?.grid),
                  min: selectedGroupCountAxisBounds.min,
                  max: selectedGroupCountAxisBounds.max,
                  ticks: {
                    ...getChartJsOptions(currentTheme).scales?.y?.ticks,
                    precision: 0,
                    callback: (value: any) => formatNumber(Math.round(Number(value || 0))),
                    stepSize: selectedGroupCountAxisBounds.stepSize,
                  },
                  title: {
                    display: !isMobileChart,
                    text: 'تعداد سفارش',
                  },
                },
              },
              plugins: {
                ...getChartJsOptions(currentTheme).plugins,
                ...(!isMobileChart ? ({
                  valueLabelPlugin: {
                    minPixelHeight: 14,
                    minAbsValue: 1,
                    avoidOverlap: false,
                    labelPosition: 'center',
                    showBackground: false,
                    textColor: 'rgba(17, 24, 39, 0.92)',
                    fontSize: 11,
                  },
                } as any) : {}),
                legend: {
                  ...getChartJsOptions(currentTheme).plugins?.legend,
                  display: !isMobileChart,
                },
                tooltip: {
                  ...getChartJsOptions(currentTheme).plugins?.tooltip,
                  callbacks: {
                    title: (tooltipItems: any) => tooltipItems?.[0]?.label || '',
                    label: (context: any) => {
                      const separator = '\u00A0:\u00A0';
                      const value = Number(context.parsed.y || 0);
                      return `${context.dataset.label}${separator}${formatNumber(Math.round(value))}`;
                    },
                  },
                },
              },
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default CustomerPurchaseBehaviorGroupCountByMonthChart;
